import type {
  Session,
  User,
  AuthChangeEvent,
  UserIdentity,
} from '@supabase/supabase-js';
import { supabase, currentStorageKey } from '../client';

export type { Session, User, AuthChangeEvent, UserIdentity };

// ---- HIPAA session-audit writers ----------------------------------------
//
// In the ideal-state production we get TWO writes per successful login:
//
//   1. The `custom_access_token` auth hook (auth-audit-hook) fires on
//      every token issuance and records the row with the IP, geo, and
//      device fingerprint extracted from the HTTPS request headers.
//
//   2. The client-side `recordSessionEvent({ kind: 'login_success' })`
//      call right after `signInWithPassword` succeeds. This is the
//      reliable path -- it runs in EVERY SPA regardless of whether the
//      Custom Access Token Hook is registered in the Supabase Auth
//      dashboard, which has bitten us before (the hook was deployed but
//      not actually selected as the project's access-token hook, so
//      `core.session_audit` saw only `logout` events for accounts where
//      MFA was enabled and `notify_on_login = true`).
//
// The DB trigger `core.fn_session_audit_login_alert` (migration
// 20260606020000_*) fires off the auth_login_alert SMS off either
// insert, so the user gets their "new sign-in" text from whichever path
// landed the row -- no double SMS because the 15-min throttle inside
// `core.fn_user_emit_security_sms` collapses concurrent inserts.
//
// The hook still owns the IP / geo / device_fingerprint columns (we
// can't get a real client IP from inside the browser). The client-side
// path leaves those null and the trigger's variables fall back to
// "Unknown" for city + empty for country.
//
// The hook STILL doesn't see explicit signOut, failed logins (the hook
// only fires on successful token issuance), or client-initiated
// password-reset requests. We wrap those flows below so every
// interesting auth event makes it to core.session_audit.
//
// All calls are best-effort; a failed audit write must NOT block the
// user from logging in, out, or resetting their password. We log to
// console so the failure is observable in browser devtools / Sentry.

interface RecordSessionEventInput {
  userId?: string | null;
  userEmail?: string | null;
  kind:
    | 'signup'
    | 'login_success'
    | 'login_failure'
    | 'logout'
    | 'password_reset_requested'
    | 'password_changed'
    | 'email_changed';
  success?: boolean;
  failureReason?: string | null;
  provider?: string | null;
}

async function recordSessionEvent(input: RecordSessionEventInput): Promise<void> {
  try {
    // p_user_id / p_user_email are NOT NULL in the signature but the
    // table allows null (failed login for unknown email). Widen the TS
    // type so we can omit them.
    const { error } = await supabase().schema('core').rpc('rpc_record_session_event', {
      p_user_id: (input.userId ?? undefined) as unknown as string,
      p_user_email: (input.userEmail ?? undefined) as unknown as string,
      p_kind: input.kind,
      p_success: input.success ?? true,
      p_failure_reason: input.failureReason ?? undefined,
      p_ip: undefined,
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      p_geo_country: undefined,
      p_geo_region: undefined,
      p_geo_city: undefined,
      p_device_fingerprint: undefined,
      p_mfa_method: undefined,
      p_provider: input.provider ?? 'email',
      p_payload: { client: 'web' },
    });
    if (error) {
       
      console.warn('[audit] rpc_record_session_event failed:', error.message);
    }
  } catch (e) {
     
    console.warn('[audit] rpc_record_session_event threw:', e);
  }
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await supabase().auth.getSession();
  if (error) throw error;
  return data.session ?? null;
}

interface MaybePersistedSession {
  access_token?: unknown;
  refresh_token?: unknown;
  expires_at?: unknown;
}

function resolveAuthStorageKey(): string | null {
  const configured = currentStorageKey();
  if (configured) return configured;
  if (typeof window === 'undefined') return null;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && /^sb-.*-auth-token$/.test(k)) return k;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Synchronously read the persisted Supabase session from localStorage at
 * AuthProvider mount — before supabase-js finishes `_emitInitialSession`.
 * Used to seed React state so returning users skip the boot-loader flash.
 */
export function readPersistedAuthSession(): Session | null {
  if (typeof window === 'undefined') return null;
  const storageKey = resolveAuthStorageKey();
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MaybePersistedSession;
    if (
      !parsed ||
      typeof parsed.access_token !== 'string' ||
      typeof parsed.refresh_token !== 'string'
    ) {
      return null;
    }
    const expiresAt =
      typeof parsed.expires_at === 'number' ? parsed.expires_at : null;
    if (expiresAt !== null && expiresAt * 1000 <= Date.now()) {
      return null;
    }
    return parsed as unknown as Session;
  } catch {
    return null;
  }
}

export async function getUser(): Promise<User | null> {
  const { data, error } = await supabase().auth.getUser();
  if (error) {
    // getUser returns 401 for anon users -- not actually an error to us.
    return null;
  }
  return data.user;
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const { data, error } = await supabase().auth.signInWithPassword({ email, password });
  if (error) {
    // The custom_access_token auth hook never fires on failure, so we
    // record login_failure here so the security trail catches brute-
    // force attempts and account-takeover probes.
    await recordSessionEvent({
      userEmail: email,
      kind: 'login_failure',
      success: false,
      failureReason: error.message,
      provider: 'email',
    });
    throw error;
  }
  if (!data.session) throw new Error('Sign-in returned no session.');
  // Record login_success directly. The DB trigger
  // `core.session_audit_login_alert` (migration 20260606020000_*) fires
  // the `auth_login_alert` SMS off this insert when the user has opted
  // in (`core.user_sms_preferences.notify_on_login = true`). This is the
  // reliable SMS path -- the auth-audit-hook path requires the Custom
  // Access Token Hook to be registered in the Supabase Auth dashboard,
  // which can drift out of config silently. The 15-min throttle inside
  // `fn_user_emit_security_sms` collapses any duplicate insert from the
  // hook so the user never gets two texts for the same login.
  await recordSessionEvent({
    userId: data.session.user?.id ?? undefined,
    userEmail: data.session.user?.email ?? email,
    kind: 'login_success',
    success: true,
    provider: 'email',
  });
  return data.session;
}

/**
 * Sign a brand-new user up via email + password.
 *
 * `emailRedirectTo` is the URL Supabase Auth puts on the {{.ConfirmationURL}}
 * link in the signup confirmation email. If omitted, Supabase falls back to
 * the project's `Site URL`, which on a fresh project defaults to
 * `http://localhost:5173` -- shipping a localhost link to real users. Always
 * pass an absolute, production URL here; the caller usually wants the app
 * origin so the user lands signed-in at home after confirming.
 */
/**
 * Sign-up shape returned by signUpWithPassword. When the Supabase project
 * has "Confirm email" enabled, `requiresOtp` is true and the caller must
 * collect the 6-digit code via `verifyEmailOtp(...)` before the user has
 * an active session. When confirmations are disabled, Supabase returns a
 * session immediately and `requiresOtp` is false.
 */
export interface SignUpResult {
  requiresOtp: boolean;
  /** Populated only when confirmations are disabled and Supabase returned a session inline. */
  session: Session | null;
}

/**
 * Caller-supplied metadata copied verbatim into `auth.users.raw_user_meta_data`.
 * Conventional keys we collect on the app-site sign-up form so downstream
 * services (`core.fn_user_email_and_name`, email templates, audit views)
 * have a real display name + a recorded TOS/Privacy acceptance from day 1
 * instead of an opaque uuid+email row.
 */
export interface SignUpUserMetadata {
  full_name?: string;
  /** Same value as `full_name`; we mirror it so older readers that only check `name` resolve too. */
  name?: string;
  /** E.164 phone collected at central sign-up (e.g. +15551234567). */
  phone?: string;
  /** ISO-8601 timestamp captured when the user ticked the TOS checkbox. */
  tos_accepted_at?: string;
  /** Versioned policy bundle the user agreed to (e.g. `'2026-05-20'`). */
  tos_version?: string;
  [k: string]: string | number | boolean | undefined;
}

export async function signUpWithPassword(
  email: string,
  password: string,
  emailRedirectTo?: string,
  userData?: SignUpUserMetadata,
): Promise<SignUpResult> {
  // Build the options object only if we actually have something to pass;
  // supabase-js treats an empty `options` differently from `undefined` in
  // some edge cases (notably it stops falling back to the project Site URL
  // for the confirmation email), so we preserve the original behavior for
  // callers that don't supply any options.
  const options: { emailRedirectTo?: string; data?: SignUpUserMetadata } = {};
  if (emailRedirectTo) options.emailRedirectTo = emailRedirectTo;
  if (userData && Object.keys(userData).length > 0) options.data = userData;
  const { data, error } = await supabase().auth.signUp({
    email,
    password,
    options: Object.keys(options).length > 0 ? options : undefined,
  });
  if (error) throw error;
  return { requiresOtp: data.session === null, session: data.session ?? null };
}

/**
 * Verify the 6-digit OTP a user typed back into the page after we sent
 * the signup confirmation email. On success the browser receives a fresh
 * session and the page stays on the same URL -- no navigation away, no
 * localhost-confirmation-link footguns.
 */
export async function verifyEmailOtp(
  email: string,
  token: string,
  type: 'signup' | 'email' | 'recovery' | 'magiclink' = 'email',
): Promise<Session> {
  const { data, error } = await supabase().auth.verifyOtp({
    email,
    token,
    type,
  });
  if (error) {
    await recordSessionEvent({
      userEmail: email,
      kind: 'login_failure',
      success: false,
      failureReason: error.message,
      provider: 'otp',
    });
    throw error;
  }
  if (!data.session) throw new Error('OTP verification returned no session.');
  // Mirror the password sign-in path: record login_success so the
  // session_audit trigger fires `auth_login_alert` SMS for opted-in
  // users. Treat recovery / magiclink as login_success too -- they all
  // mint a fresh session from the user's perspective.
  await recordSessionEvent({
    userId: data.session.user?.id ?? undefined,
    userEmail: data.session.user?.email ?? email,
    kind: 'login_success',
    success: true,
    provider: 'otp',
  });
  return data.session;
}

/**
 * Resend a fresh OTP for an in-flight signup. Useful when the first email
 * never arrived or the user let the 10-minute window lapse.
 */
export async function resendSignupOtp(email: string): Promise<void> {
  const { error } = await supabase().auth.resend({
    type: 'signup',
    email,
  });
  if (error) throw error;
}

/**
 * Idempotent password-based authentication. Tries to sign in first; if the
 * email isn't registered yet, falls through to signUp. This is the shape
 * every "Apply to sell / Sign up" path on the vendor + shop surfaces wants
 * so that an operator who already has a `@pc/web` account can reuse the
 * same email (and password) to gain a vendor profile -- they should never
 * see "must use a different email".
 *
 * Return:
 *  - `kind: 'signed-in'`   the email already existed and the password was
 *                          correct. `session` is populated.
 *  - `kind: 'wrong-password'`  the email exists but the password didn't
 *                              match. Caller should show "this email is
 *                              already on file -- use your existing
 *                              password" and let the user retry.
 *  - `kind: 'awaiting-otp'`  brand-new user; an OTP email is on its way.
 *                            Caller advances to the code-entry step and
 *                            calls `verifyEmailOtp` next.
 *  - `kind: 'signed-up'`   brand-new user AND the Supabase project has
 *                          confirmations off, so Supabase returned a
 *                          session inline. No OTP step needed.
 */
export type AuthAttemptResult =
  | { kind: 'signed-in'; session: Session }
  | { kind: 'wrong-password' }
  | { kind: 'awaiting-otp' }
  | { kind: 'signed-up'; session: Session };

export async function signInOrSignUpWithPassword(
  email: string,
  password: string,
  emailRedirectTo?: string,
): Promise<AuthAttemptResult> {
  const signIn = await supabase().auth.signInWithPassword({ email, password });
  if (signIn.data.session) {
    // Same login_success audit as signInWithPassword above so the
    // session_audit trigger fires the login-alert SMS path.
    await recordSessionEvent({
      userId: signIn.data.session.user?.id ?? undefined,
      userEmail: signIn.data.session.user?.email ?? email,
      kind: 'login_success',
      success: true,
      provider: 'email',
    });
    return { kind: 'signed-in', session: signIn.data.session };
  }
  // We get here when Supabase returned an error. Two cases matter:
  //  - "Invalid login credentials" -- might be a wrong password for an
  //    existing user, OR a brand-new email. Try signUp to disambiguate.
  //  - anything else -- propagate so the UI can show it.
  const message = signIn.error?.message ?? '';
  const looksLikeInvalidCreds =
    /invalid login credentials/i.test(message) ||
    /invalid_credentials/i.test(message);
  if (!looksLikeInvalidCreds) {
    if (signIn.error) throw signIn.error;
    throw new Error('Sign-in returned no session.');
  }

  // Probe signUp. If the email is already registered, Supabase returns
  // a user-without-identities (older API) or a clear "user already
  // registered" error (newer API). Either way, that's the
  // wrong-password case.
  const signUp = await supabase().auth.signUp({
    email,
    password,
    options: emailRedirectTo ? { emailRedirectTo } : undefined,
  });
  if (signUp.error) {
    const m = signUp.error.message ?? '';
    if (/already registered/i.test(m) || /user already exists/i.test(m)) {
      return { kind: 'wrong-password' };
    }
    throw signUp.error;
  }
  // supabase-js returns `user` with empty `identities` array when the
  // email is already on file (a privacy-preserving signal so we don't
  // leak account existence). Treat that as wrong-password too.
  if (signUp.data.user && Array.isArray((signUp.data.user as { identities?: unknown[] }).identities) &&
      ((signUp.data.user as { identities?: unknown[] }).identities ?? []).length === 0) {
    return { kind: 'wrong-password' };
  }
  if (signUp.data.session) {
    // Brand-new account with inline session (Supabase project has
    // confirmations off). Record as a signup AND a login_success so the
    // /qa/observed signup predicates AND the login-alert pipeline fire.
    await recordSessionEvent({
      userId: signUp.data.session.user?.id ?? undefined,
      userEmail: signUp.data.session.user?.email ?? email,
      kind: 'signup',
      success: true,
      provider: 'email',
    });
    await recordSessionEvent({
      userId: signUp.data.session.user?.id ?? undefined,
      userEmail: signUp.data.session.user?.email ?? email,
      kind: 'login_success',
      success: true,
      provider: 'email',
    });
    return { kind: 'signed-up', session: signUp.data.session };
  }
  return { kind: 'awaiting-otp' };
}

// ---- OAuth / social identity (Google) -----------------------------------
//
// We support "Continue with Google" two ways:
//
//   1. Sign-in / sign-up -- `signInWithGoogle` kicks off the OAuth
//      redirect. supabase-js's `detectSessionInUrl` (enabled in
//      `initSupabase`) auto-consumes the provider response when the user
//      lands back on `redirectTo`, mints the session, and fires
//      `SIGNED_IN`. Because that auto-consume path bypasses our
//      `signInWithPassword` audit writer, callers must invoke
//      `maybeRecordOAuthLogin(session)` from their AuthProvider's
//      `SIGNED_IN` handler so the `core.session_audit` row + login-alert
//      SMS fire exactly like a password sign-in.
//
//   2. Account linking -- `linkGoogleIdentity` attaches a Google identity
//      to the CURRENTLY signed-in user (Supabase "manual linking" must be
//      enabled on the project). Same redirect/auto-consume shape; the
//      session user is unchanged, just gains a new identity row.
//
// The "pending" sessionStorage markers below survive the full-page
// redirect to Google and back (sessionStorage is per-origin and the
// round-trip returns to the same origin), so the post-return handler can
// tell an OAuth-originated `SIGNED_IN` apart from a routine token refresh
// or tab-visibility re-emit -- neither of which should write an audit row.

const OAUTH_LOGIN_PENDING_KEY = 'pc.oauth.login_pending';

/** Provider slugs we support for social auth. Narrow on purpose. */
export type OAuthProvider = 'google';

/**
 * Begin a "Continue with Google" sign-in. Performs a full-page redirect to
 * Google's consent screen; supabase-js navigates the browser itself (we do
 * not pass `skipBrowserRedirect`). On return to `redirectTo`, supabase-js
 * auto-detects the session in the URL and fires `SIGNED_IN`.
 *
 * `redirectTo` MUST be registered in the Supabase dashboard's
 * Authentication > URL Configuration > "Redirect URLs" allow-list, else
 * Supabase rejects the callback.
 *
 * `prompt: 'select_account'` forces Google's account chooser every time
 * instead of silently reusing the one Google session the browser already
 * has -- important on shared machines and for users with multiple Gmails.
 */
export async function signInWithGoogle(redirectTo: string): Promise<void> {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(OAUTH_LOGIN_PENDING_KEY, 'google');
    }
  } catch {
    // sessionStorage can be blocked; the audit write just won't fire.
  }
  const { error } = await supabase().auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      queryParams: { prompt: 'select_account' },
    },
  });
  if (error) {
    // The redirect never happened, so clear the marker we optimistically set.
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(OAUTH_LOGIN_PENDING_KEY);
      }
    } catch {
      // ignore
    }
    throw error;
  }
}

/**
 * Record a `login_success` audit row for an OAuth-originated session, but
 * ONLY when this browser actually just completed a `signInWithGoogle`
 * round-trip (gated on the `pc.oauth.login_pending` marker). Idempotent:
 * the marker is cleared on first call so a later `TOKEN_REFRESHED` /
 * visibility-change `SIGNED_IN` re-emit is a no-op.
 *
 * Safe to call on EVERY `SIGNED_IN` event; it self-gates and never throws
 * (audit is observability, not a correctness gate).
 */
export async function maybeRecordOAuthLogin(session: Session | null): Promise<void> {
  if (!session) return;
  if (typeof window === 'undefined') return;
  let pending: string | null = null;
  try {
    pending = window.sessionStorage.getItem(OAUTH_LOGIN_PENDING_KEY);
  } catch {
    return;
  }
  if (!pending) return;
  try {
    window.sessionStorage.removeItem(OAUTH_LOGIN_PENDING_KEY);
  } catch {
    // ignore
  }
  await recordSessionEvent({
    userId: session.user?.id ?? undefined,
    userEmail: session.user?.email ?? undefined,
    kind: 'login_success',
    success: true,
    provider: pending,
  });
}

/**
 * Attach a Google identity to the currently signed-in user. Requires
 * "Allow manual linking" enabled on the Supabase project. Performs the
 * same full-page redirect as `signInWithGoogle`; on return the session
 * user is unchanged but `getUserIdentities()` will include the new Google
 * identity. `redirectTo` must be in the project's Redirect URLs allow-list.
 */
export async function linkGoogleIdentity(redirectTo: string): Promise<void> {
  const { error } = await supabase().auth.linkIdentity({
    provider: 'google',
    options: { redirectTo },
  });
  if (error) throw error;
}

/**
 * List the signed-in user's linked identities (e.g. the original `email`
 * identity plus any linked `google` identity). Used by the "Connected
 * accounts" settings surface.
 */
export async function listMyIdentities(): Promise<UserIdentity[]> {
  const { data, error } = await supabase().auth.getUserIdentities();
  if (error) throw error;
  return data?.identities ?? [];
}

/**
 * Unlink a previously-linked identity from the signed-in user. Supabase
 * refuses to unlink the user's only remaining identity server-side (it
 * would orphan the account), but callers should also guard in the UI so
 * the user never sees a confusing "can't unlink" error.
 */
export async function unlinkMyIdentity(identity: UserIdentity): Promise<void> {
  const { error } = await supabase().auth.unlinkIdentity(identity);
  if (error) throw error;
}

export async function sendMagicLink(email: string, emailRedirectTo: string): Promise<void> {
  const { error } = await supabase().auth.signInWithOtp({
    email,
    options: { emailRedirectTo },
  });
  if (error) throw error;
}

/**
 * Trigger Supabase's password-reset email. The recipient receives a link to
 * `redirectTo` with a recovery token; opening it puts the browser in a
 * recovery session (the AuthChangeEvent is 'PASSWORD_RECOVERY'), at which
 * point the app should prompt for a new password and call `updatePassword`.
 *
 * The email template itself lives in the Supabase dashboard
 * (Auth -> Email Templates -> "Reset Password") and is sourced from
 * `supabase/email-templates/reset-password.html`.
 */
export async function resetPasswordForEmail(
  email: string,
  redirectTo: string,
): Promise<void> {
  const { error } = await supabase().auth.resetPasswordForEmail(email, {
    redirectTo,
  });
  if (error) throw error;
  await recordSessionEvent({
    userEmail: email,
    kind: 'password_reset_requested',
    success: true,
    provider: 'email',
  });
}

/**
 * Update the signed-in user's password. Valid both after a normal sign-in
 * AND inside a PASSWORD_RECOVERY session (which is what makes the
 * /reset-password flow work end-to-end).
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const { error } = await supabase().auth.updateUser({ password: newPassword });
  if (error) throw error;
  const { data: u } = await supabase().auth.getUser();
  await recordSessionEvent({
    userId: u.user?.id ?? undefined,
    userEmail: u.user?.email ?? undefined,
    kind: 'password_changed',
    success: true,
  });
}

/**
 * Update the signed-in user's email. Supabase sends a confirmation email to
 * the new address; the change does not take effect until the user clicks
 * the link.
 */
export async function updateEmail(newEmail: string): Promise<void> {
  const { data: before } = await supabase().auth.getUser();
  const { error } = await supabase().auth.updateUser({ email: newEmail });
  if (error) throw error;
  await recordSessionEvent({
    userId: before.user?.id ?? undefined,
    userEmail: before.user?.email ?? undefined,
    kind: 'email_changed',
    success: true,
  });
}

/**
 * Sign-out scope.
 *
 *   - `'global'` (default) revokes the user's refresh token on the
 *     server via `POST /auth/v1/logout?scope=global`. This also kills
 *     any other tab / device the same refresh token is alive in.
 *   - `'local'` skips the network call entirely and only clears local
 *     storage. Useful for the central `auth.peptideclients.com/signout`
 *     page, where the originating B2B app already called `'global'`.
 *   - `'others'` revokes every refresh token EXCEPT the current one.
 */
export interface SignOutInput {
  scope?: 'global' | 'local' | 'others';
}

// In-flight sign-out sentinel ------------------------------------------------
//
// PROBLEM: when a B2B app's `AuthProvider.signOut` runs
//
//   await signOut();              // fires SIGNED_OUT -> setSession(null)
//   setSession(null);             // React re-render
//   window.location.replace(authSignoutUrl);
//
// the `SIGNED_OUT` broadcast from supabase-js causes React to re-render
// with `session=null` BEFORE the final `window.location.replace` line
// runs. The B2B's `RequireAuth` then sees `session=null` and issues
// its OWN `window.location.replace(authLoginUrl)`. That second redirect
// wins the race, the user lands on `auth/login` instead of `auth/signout`,
// `pc-auth.auth` is never wiped, and the next nav silently re-issues
// a stale token via the "pre-existing session" hand-off effect.
//
// FIX: AuthProvider.signOut calls `markSignOutInFlight()` BEFORE the
// `await signOut()`. RequireAuth checks `isSignOutInFlight()` before
// running its redirect-to-login branch -- if true, it short-circuits
// to a Loading state and lets AuthProvider.signOut win the navigation
// race. The flag is timestamped and self-expires after 8 seconds in
// case the navigation never completes (e.g. user closed the tab).
//
// Stored in sessionStorage rather than module-level state because the
// B2B `signOut` handler awaits `@pc/db` `signOut()` -- which fires the
// SIGNED_OUT broadcast -- and React's re-render might happen in a
// different component-tree timing than the calling tick. sessionStorage
// is synchronous and survives the re-render.

const SIGN_OUT_SENTINEL_KEY = 'pc.signingOut';
const SIGN_OUT_SENTINEL_TTL_MS = 8_000;

export function markSignOutInFlight(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SIGN_OUT_SENTINEL_KEY, String(Date.now()));
  } catch {
    // sessionStorage can be disabled in embedded/private contexts.
  }
}

export function clearSignOutInFlight(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SIGN_OUT_SENTINEL_KEY);
  } catch {
    // ignore
  }
}

export function isSignOutInFlight(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(SIGN_OUT_SENTINEL_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    if (Date.now() - ts > SIGN_OUT_SENTINEL_TTL_MS) {
      try {
        window.sessionStorage.removeItem(SIGN_OUT_SENTINEL_KEY);
      } catch {
        // ignore
      }
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Sign the current user out.
 *
 * This wrapper is intentionally more aggressive than
 * `supabase().auth.signOut()` directly:
 *
 *   1. Captures user identity BEFORE clearing the session so the
 *      `core.session_audit` row is correctly attributed.
 *   2. Manually wipes EVERY localStorage key matching the configured
 *      `storageKey` prefix as a fallback. supabase-js intentionally
 *      leaves storage intact when its server-side revoke call fails
 *      with a non-401/403/404 error (5xx, network blip, CORS), which
 *      in our SSO architecture causes "I clicked sign-out and I'm
 *      still signed in" because the next nav silently re-issues a
 *      session from the still-present refresh token.
 *   3. Fires the audit row best-effort and never rethrows from it.
 *
 * Never throws. The user's local session WILL be gone by the time
 * this returns; callers don't need defensive error handling for the
 * common case.
 */
export async function signOut(opts?: SignOutInput): Promise<void> {
  const scope: 'global' | 'local' | 'others' = opts?.scope ?? 'global';

  let userId: string | null = null;
  let userEmail: string | null = null;
  try {
    const { data: u } = await supabase().auth.getUser();
    userId = u.user?.id ?? null;
    userEmail = u.user?.email ?? null;
  } catch {
    // A missing/expired session is a normal pre-condition for sign-out;
    // we still want to clear local storage below.
  }

  let supabaseError: unknown = null;
  try {
    const { error } = await supabase().auth.signOut({ scope });
    if (error) supabaseError = error;
  } catch (err) {
    supabaseError = err;
  }

  // Defense-in-depth: manually wipe every localStorage entry under our
  // storageKey prefix. supabase-js writes the main session blob at
  // `<storageKey>` plus auxiliary keys (`<storageKey>-code-verifier`,
  // etc) with the same prefix. This catches the case where the
  // server-side revoke call above failed and supabase-js bailed
  // before its own `_removeSession()`.
  try {
    const prefix = currentStorageKey();
    if (prefix && typeof window !== 'undefined' && window.localStorage) {
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(prefix)) toRemove.push(k);
      }
      for (const k of toRemove) window.localStorage.removeItem(k);
    }
    // Also wipe the cross-origin MFA local hint store. It's keyed by
    // session_id and a fresh sign-in produces a fresh session_id, so
    // a stale hint can never authorise a future session; but clearing
    // it here keeps the localStorage surface honest and avoids
    // surprising "this device remembers MFA on a different account"
    // bugs after an account switch.
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem('pc.mfa.local_verified.v1');
      } catch {
        // ignore
      }
    }
  } catch {
    // localStorage can be blocked in embedded contexts (some webviews,
    // strict privacy modes). Nothing we can do — fall through.
  }

  // Best-effort audit. We don't want a transient DB failure to surface
  // as a sign-out error to the user; their local session is already
  // gone by this point and that's the contract.
  void recordSessionEvent({
    userId,
    userEmail,
    kind: 'logout',
    success: supabaseError == null,
    provider: 'email',
  }).catch(() => {
    // swallow — audit is observability, not a correctness gate
  });
}

export function onAuthStateChange(
  cb: (event: AuthChangeEvent, session: Session | null) => void,
): () => void {
  const { data } = supabase().auth.onAuthStateChange(cb);
  return () => data.subscription.unsubscribe();
}

/**
 * Content-equality for two Session values.
 *
 * Why this exists: `@supabase/auth-js` registers a `visibilitychange`
 * listener that re-emits `SIGNED_IN` with a freshly-parsed Session every
 * time the tab becomes visible, even when nothing has actually changed.
 * Forwarding those events straight into React state via `setSession(s)`
 * creates a new reference on every tab focus, which cascades through
 * every `useAuth()` consumer (new context value -> re-render) and can
 * flush brittle route-gate state machines through a one-frame Loading
 * branch -- unmounting the matched page tree and resetting any
 * in-progress form. AuthProviders should use this helper inside a
 * functional updater so a no-op event becomes a literal state no-op:
 *
 *     onAuthStateChange((_event, next) => {
 *       setSession((prev) => (sessionsEqual(prev, next) ? prev : next));
 *     });
 *
 * Real sign-in / sign-out / token rotation all flip at least one of the
 * fields below and propagate normally.
 */
export function sessionsEqual(
  a: Session | null,
  b: Session | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.access_token === b.access_token &&
    a.refresh_token === b.refresh_token &&
    a.expires_at === b.expires_at &&
    a.user?.id === b.user?.id
  );
}
